import {
  Box,
  Card,
  CardMedia,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Grid,
  Chip,
  Fade,
  Zoom,
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { useContext, useEffect, useState } from "react";
import { BackendBaseUrl, AuthContext, ORANGE, ORANGE_HOVER, BG_LIGHT, PREMIUM_SHADOW, CARD_SHADOW } from "./global_varibles";
import HeaderAppBar from "./appbar";
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import ScrollToTopButton from "./scrool_button";
import { useNavigate } from "react-router-dom";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { handleAddItem,handleRemoveItem,handleNewItemChange,handleDecrement,handleIncrement, handleUserDetailsChange, handleDateChange, handleCategorySelect, handlePriceChange, handleQuantityChange } from "./order_common_func";
import dayjs from "dayjs"; // Assuming dayjs is being used for date handling
import type { Dayjs } from "dayjs"; // <-- add type import
import { alpha, styled } from "@mui/material/styles";

type SubCategoryKey = string;
type ItemId = string;
type CategoryKey = string;

// Item names (lowercase) that should accept fractional quantities (e.g. "2.5")
// instead of whole-number-only counts.
const DECIMAL_QTY_ITEM_NAMES = ["laundry by weight", "curtain"];

interface CategoryDetails {
  type_id: string | number;
  serv_id: string | number;
  item_price: number;
}

interface ItemValues {
  item_url: string;
  item_name: string;
  categories: Record<SubCategoryKey, CategoryDetails>;
}

type OrderData = Record<CategoryKey, Record<ItemId, ItemValues>>;

interface NewItem {
  itemName: string;
  quantity: number | string;
  unitPrice: number | string;
  totalPrice: number | string;
}

interface UserDetails {
  name: string;
  phone: string;
  address: string;
  deliveryDate: Dayjs; // keep Dayjs in state
}

const StyledAlphabetNav = styled(Box)(({ theme }) => ({
  position: 'fixed',
  right: '12px',
  top: '55%',
  transform: 'translateY(-50%)',
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  zIndex: 999,
  backgroundColor: alpha('#fff', 0.8),
  backdropFilter: 'blur(8px)',
  borderRadius: '20px',
  padding: '8px 4px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  border: `1px solid ${alpha(ORANGE, 0.1)}`,
  maxHeight: '70vh',
  overflowY: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    right: '4px',
    top: '55%',
    padding: '4px 2px',
  }
}));

const AlphabetNavigation = ({ 
  orderData, 
  selectedCategory, 
  onLetterClick 
}: { 
  orderData: OrderData;
  selectedCategory: string;
  onLetterClick: (letter: string) => void;
}) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  
  const getAvailableLetters = () => {
    if (!orderData[selectedCategory]) return new Set();
    
    const availableLetters = new Set<string>();
    Object.values(orderData[selectedCategory]).forEach(item => {
      const firstLetter = item.item_name.charAt(0).toUpperCase();
      if (alphabet.includes(firstLetter)) {
        availableLetters.add(firstLetter);
      }
    });
    return availableLetters;
  };

  const availableLetters = getAvailableLetters();

  return (
    <StyledAlphabetNav>
      {alphabet.map((letter) => (
        <IconButton
          key={letter}
          size="small"
          onClick={() => onLetterClick(letter)}
          disabled={!availableLetters.has(letter)}
          sx={{
            width: { xs: 20, sm: 26 },
            height: { xs: 20, sm: 26 },
            padding: 0,
            fontSize: { xs: '0.65rem', sm: '0.75rem' }, 
            fontWeight: 700,
            color: availableLetters.has(letter) ? '#475569' : '#cbd5e1',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: alpha(ORANGE, 0.1),
              color: ORANGE,
            },
            '&.Mui-disabled': {
              color: '#f1f5f9',
            },
          }}
        >
          {letter}
        </IconButton>
      ))}
    </StyledAlphabetNav>
  );
};

function GenerateOrderCards({ order_data }: { order_data: OrderData }) {
  const navigate = useNavigate();

  const [newItems, setNewItems] = useState<NewItem[]>(() => {
    const storedNewItems = localStorage.getItem("new_items");
    return storedNewItems ? (JSON.parse(storedNewItems) as NewItem[]) : [];
  });

  const [items, setItems] = useState<any>(() => {
    const storedItems = localStorage.getItem("current_order_items");
    if (storedItems) {
      return JSON.parse(storedItems);
    }
    const initialItems: any = {};
    Object.entries(order_data).forEach(([category, categoryDetails]) => {
      initialItems[category] = {};
      Object.entries(categoryDetails).forEach(([item_id, itemDetails]) => {
        initialItems[category][item_id] = { selectedCategory: null as string | null };
        Object.entries(itemDetails.categories).forEach(
          ([category_key, category_value]) => {
            initialItems[category][item_id][category_key] = {
              type_id: category_value.type_id,
              serv_id: category_value.serv_id,
              quantity: 0,
              price: category_value.item_price,
            };
          }
        );
      });
    });
    return initialItems;
  });

  const initialCategory: string =
    Object.keys(order_data ?? {})[0] ?? "";

  const [selectedCategory, setSelectedCategory] = useState<string>(initialCategory);

  const [user_details, setUserDetails] = useState<UserDetails>(() => {
    const savedStr = localStorage.getItem("user_details");
    if (savedStr) {
      const parsed = JSON.parse(savedStr) as {
        name?: string;
        phone?: string;
        address?: string;
        deliveryDate?: string;
      };
      return {
        name: parsed.name ?? "",
        phone: parsed.phone ?? "",
        address: parsed.address ?? "",
        deliveryDate: parsed.deliveryDate ? dayjs(parsed.deliveryDate) : dayjs(),
      };
    }
    return {
      name: "",
      phone: "",
      address: "",
      deliveryDate: dayjs(),
    };
  });



  // Save user_details to localStorage whenever it changes
  useEffect(() => {
    // Serialize Dayjs explicitly to ISO string to avoid non-serializable values
    const serializable = {
      ...user_details,
      deliveryDate: user_details.deliveryDate.toISOString(),
    };
    localStorage.setItem("user_details", JSON.stringify(serializable));
  }, [user_details]);




  // Handle category filter selection
  const handleFilterChange = (category: string) => {
    setSelectedCategory(category);
  };

  const handleOrderSummary = () => {
    const today = dayjs();
    if (!user_details.phone || user_details.phone.length !== 10) {
      alert("enter valid Phone number and Delivery Date are required.");
      return;
    }
    if (!user_details.deliveryDate.isAfter(today, "day")) {
      alert("Delivery date must be in the future. Please select a valid date.");
      return;
    }
    localStorage.setItem("current_order_items", JSON.stringify(items));
    localStorage.setItem("new_items", JSON.stringify(newItems));
    navigate("/order-summary");
  };

  // Remove the selectedLetter state since we're not filtering anymore
  // const [selectedLetter, setSelectedLetter] = useState<string | null>(null);

  // Modify this function to account for the fixed app bar
  const handleLetterClick = (letter: string) => {
    // Find first item starting with this letter
    const categoryItems = Object.entries(order_data[selectedCategory] ?? {});
    const targetItem = categoryItems.find(([_, itemValues]) => 
      itemValues.item_name.charAt(0).toUpperCase() === letter
    );
    
    if (targetItem) {
      const [itemId] = targetItem;
      // Scroll to the item
      const element = document.getElementById(`item-${itemId}`);
      if (element) {
        // Get the element's position relative to the document
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        // Account for the app bar height (usually around 64px) plus some extra padding
        const offsetPosition = elementPosition - 80;
        
        // Scroll to the calculated position
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  // Remove the filtering function since we want to show all items
  // const getFilteredItems = () => {
  //   const categoryItems = Object.entries(order_data[selectedCategory] ?? {});
  //   
  //   if (!selectedLetter) {
  //     return categoryItems;
  //   }
  //   
  //   return categoryItems.filter(([_, itemValues]) => 
  //     itemValues.item_name.charAt(0).toUpperCase() === selectedLetter
  //   );
  // };

  return (
    <Box sx={{ width: "100%", bgcolor: BG_LIGHT, minHeight: '100vh', pb: 8 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
          mt: { xs: 0, sm: 3 },
          p: { xs: 2, sm: 4 },
          boxSizing: "border-box",
          borderRadius: { xs: 0, sm: 4 },
          boxShadow: PREMIUM_SHADOW,
          overflow: 'hidden',
          backgroundColor: '#fff',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 4, color: '#1e293b', textAlign: 'center' }}>
          Finalize Your Order
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={3} sx={{ maxWidth: 1000, mx: 'auto' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Full Name"
                name="name"
                variant="outlined"
                value={user_details.name}
                onChange={(e) => handleUserDetailsChange(setUserDetails, e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={{ color: ORANGE }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  "& .MuiOutlinedInput-root": { 
                    borderRadius: 3,
                    bgcolor: alpha(BG_LIGHT, 0.5),
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: alpha(BG_LIGHT, 0.8) },
                    '&.Mui-focused': { bgcolor: '#fff' }
                  } 
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                type="number"
                required
                variant="outlined"
                value={user_details.phone}
                onChange={(e) => handleUserDetailsChange(setUserDetails, e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIphoneIcon sx={{ color: ORANGE }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  "& .MuiOutlinedInput-root": { 
                    borderRadius: 3,
                    bgcolor: alpha(BG_LIGHT, 0.5),
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: alpha(BG_LIGHT, 0.8) },
                    '&.Mui-focused': { bgcolor: '#fff' }
                  } 
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="Delivery Date"
                value={user_details.deliveryDate}
                onChange={(newValue) =>
                  handleDateChange(setUserDetails, newValue ?? dayjs())
                }
                slotProps={{ 
                  textField: { 
                    fullWidth: true, 
                    required: true,
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarMonthOutlinedIcon sx={{ color: ORANGE }} />
                        </InputAdornment>
                      ),
                    }
                  } 
                }}
                sx={{ 
                  "& .MuiOutlinedInput-root": { 
                    borderRadius: 3,
                    bgcolor: alpha(BG_LIGHT, 0.5),
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: alpha(BG_LIGHT, 0.8) },
                    '&.Mui-focused': { bgcolor: '#fff' }
                  } 
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Pickup Address"
                name="address"
                variant="outlined"
                value={user_details.address}
                onChange={(e) => handleUserDetailsChange(setUserDetails, e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnOutlinedIcon sx={{ color: ORANGE }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  "& .MuiOutlinedInput-root": { 
                    borderRadius: 3,
                    bgcolor: alpha(BG_LIGHT, 0.5),
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: alpha(BG_LIGHT, 0.8) },
                    '&.Mui-focused': { bgcolor: '#fff' }
                  } 
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        {/* Add Button */}
        <Box sx={{ mt: 6, mb: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleAddItem(setNewItems)}
            sx={{
              bgcolor: ORANGE,
              fontWeight: 800,
              px: 4,
              py: 1.5,
              borderRadius: 3,
              textTransform: "none",
              fontSize: '1rem',
              boxShadow: `0 8px 16px ${alpha(ORANGE, 0.25)}`,
              transition: 'all 0.3s',
              "&:hover": { 
                bgcolor: ORANGE_HOVER,
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 20px ${alpha(ORANGE, 0.35)}`,
              },
            }}
          >
            Add Custom Item
          </Button>

          {/* Dynamic Item Rows */}
          <Box sx={{ mt: 4, maxWidth: 800, mx: 'auto' }}>
            {newItems.map((item, index) => (
              <Zoom in key={index}>
                <Box
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: alpha(BG_LIGHT, 0.5),
                    borderRadius: 4,
                    border: `1px solid ${alpha('#e2e8f0', 0.8)}`,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    alignItems: "center",
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  }}
                >
                  <TextField
                    label="Item Name"
                    size="small"
                    fullWidth
                    value={item.itemName}
                    onChange={(e) => handleNewItemChange(setNewItems, index, "itemName", e.target.value)}
                    sx={{ flex: 3, "& .MuiOutlinedInput-root": { borderRadius: 3, bgcolor: '#fff' } }}
                  />
                  <TextField
                    label="Qty"
                    size="small"
                    type="number"
                    value={item.quantity === 0 ? "" : item.quantity}
                    onChange={(e) => {
                      const newQty = e.target.value;
                      handleNewItemChange(setNewItems, index, "quantity", newQty);
                      // Live update totalPrice
                      const q = Number(newQty) || 0;
                      const p = Number(item.unitPrice) || 0;
                      handleNewItemChange(setNewItems, index, "totalPrice", parseFloat((q * p).toFixed(2)));
                    }}
                    inputProps={{ step: "any" }}
                    sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 3, bgcolor: '#fff' } }}
                  />
                  <TextField
                    label="Unit Price"
                    size="small"
                    type="number"
                    value={item.unitPrice === 0 ? "" : item.unitPrice}
                    onChange={(e) => {
                      const newPrice = e.target.value;
                      handleNewItemChange(setNewItems, index, "unitPrice", newPrice);
                      // Live update totalPrice
                      const q = Number(item.quantity) || 0;
                      const p = Number(newPrice) || 0;
                      handleNewItemChange(setNewItems, index, "totalPrice", parseFloat((q * p).toFixed(2)));
                    }}
                    InputProps={{ inputProps: { step: "any" }, startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 3, bgcolor: '#fff' } }}
                  />
                  <IconButton 
                    onClick={() => handleRemoveItem(setNewItems, index)}
                    sx={{ color: '#ef4444', bgcolor: '#fff', '&:hover': { bgcolor: '#fef2f2' } }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Zoom>
            ))}
          </Box>
        </Box>

        {/* Category Selection */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 6,
            mt: 6,
            flexWrap: "wrap",
            gap: 2,
            px: { xs: 2, sm: 4 },
          }}
        >
          {Object.keys(order_data).map((category) => (
            <Chip
              key={category}
              label={category}
              onClick={() => handleFilterChange(category)}
              variant={selectedCategory === category ? "filled" : "outlined"}
              sx={{
                px: 2,
                py: 2.5,
                fontSize: '0.95rem',
                fontWeight: 600,
                borderRadius: '12px',
                borderColor: ORANGE,
                bgcolor: selectedCategory === category ? ORANGE : "transparent",
                color: selectedCategory === category ? "#fff" : ORANGE,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  bgcolor: selectedCategory === category ? ORANGE_HOVER : alpha(ORANGE, 0.1),
                  transform: 'translateY(-2px)',
                  boxShadow: selectedCategory === category ? `0 4px 12px ${alpha(ORANGE, 0.4)}` : 'none',
                },
                '& .MuiChip-label': { px: 2 }
              }}
            />
          ))}
        </Box>

        {/* Items List */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fill, minmax(450px, 1fr))" },
            gap: 3,
            px: { xs: 1, sm: 4 },
            mb: 12,
          }}
        >
          {Object.entries(order_data[selectedCategory] ?? {}).map(([item_id, item_values]) => {
            return (
              <Fade in key={item_id} timeout={500}>
                <Card
                  id={`item-${item_id}`}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: CARD_SHADOW,
                    border: '1px solid transparent',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: PREMIUM_SHADOW,
                      borderColor: alpha(ORANGE, 0.1),
                    },
                  }}
                >
                  {/* Left Side - Image */}
                  <Box sx={{ 
                    width: { xs: 100, sm: 140 }, 
                    bgcolor: alpha(ORANGE, 0.03), 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    p: 1
                  }}>
                    <CardMedia
                      component="img"
                      image={item_values.item_url}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>

                  {/* Right Side - Content */}
                  <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#334155', 
                        lineHeight: 1.2, 
                        mb: 1.5,
                        fontSize: { xs: '1rem', sm: '1.1rem' } 
                      }}
                    >
                      {item_values.item_name}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 'auto' }}>
                      {Object.entries(item_values.categories).map(([subCategory]) => (
                        <Button
                          key={subCategory}
                          variant="outlined"
                          size="small"
                          onClick={() => handleCategorySelect(setItems, selectedCategory, item_id, subCategory, order_data)}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            minWidth: 0,
                            px: 1.5,
                            py: 0.5,
                            borderColor: items[selectedCategory][item_id]?.selectedCategory === subCategory ? ORANGE : '#e2e8f0',
                            bgcolor: items[selectedCategory][item_id]?.selectedCategory === subCategory ? alpha(ORANGE, 0.05) : "transparent",
                            color: items[selectedCategory][item_id]?.selectedCategory === subCategory ? ORANGE : '#64748b',
                            '&:hover': {
                              borderColor: ORANGE,
                              bgcolor: alpha(ORANGE, 0.08),
                            }
                          }}
                        >
                          {subCategory}
                        </Button>
                      ))}
                    </Box>

                    {/* Bottom Row - Pricing & Qty */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                      {Object.entries(item_values.categories).map(([subCategory]) => {
                        const isSelectedCategory = items[selectedCategory][item_id]?.selectedCategory === subCategory;
                        if (!isSelectedCategory) return null;

                        const item_price = items[selectedCategory]?.[item_id]?.[subCategory]?.price ?? 0;
                        const item_qty = items[selectedCategory]?.[item_id]?.[subCategory]?.quantity ?? 0;
                        const isDecimalQty = DECIMAL_QTY_ITEM_NAMES.includes(item_values.item_name?.trim().toLowerCase());
                        const qtyStep = isDecimalQty ? 0.5 : 1;

                        return (
                          <Box key={subCategory} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TextField
                                size="small"
                                label="Price"
                                value={item_price ?? ""}
                                onChange={(e) => handlePriceChange(setItems, selectedCategory, item_id, subCategory, e.target.value)}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Typography sx={{ color: ORANGE, fontWeight: 700 }}>₹</Typography>
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{ width: 100, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                              />
                              <Typography variant="caption" sx={{ color: '#94a3b8' }}>/ unit</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: alpha(ORANGE, 0.08), borderRadius: '10px', p: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleDecrement(setItems, selectedCategory, item_id, subCategory, qtyStep)}
                                sx={{ color: ORANGE, '&:hover': { bgcolor: alpha(ORANGE, 0.1) } }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              {isDecimalQty ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={item_qty === 0 ? "" : item_qty}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value);
                                    handleQuantityChange(setItems, selectedCategory, item_id, subCategory, isNaN(val) ? 0 : val);
                                  }}
                                  inputProps={{ step: 0.1, min: 0, style: { textAlign: 'center', fontWeight: 700, color: '#334155', padding: '4px 0' } }}
                                  sx={{
                                    width: 64,
                                    mx: 0.5,
                                    "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: '#fff' },
                                    "& fieldset": { border: 'none' },
                                  }}
                                />
                              ) : (
                                <Typography sx={{ px: 1.5, fontWeight: 700, color: '#334155', minWidth: '30px', textAlign: 'center' }}>
                                  {item_qty}
                                </Typography>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => handleIncrement(setItems, selectedCategory, item_id, subCategory, qtyStep)}
                                sx={{ color: ORANGE, '&:hover': { bgcolor: alpha(ORANGE, 0.1) } }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Card>
              </Fade>
            );
          })}
        </Box>
    {/* Add Alphabet Navigation */}
          <AlphabetNavigation
            orderData={order_data}
            selectedCategory={selectedCategory}
            onLetterClick={handleLetterClick}
          />

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              onClick={handleOrderSummary}
              sx={{
                width: { xs: "100%", sm: "400px" },
                position: "fixed",
                bottom: 24,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1000,
                borderRadius: '16px',
                backgroundColor: '#1e293b',
                color: '#fff',
                fontSize: '1.1rem',
                fontWeight: 800,
                py: 2,
                textTransform: 'none',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': { 
                  backgroundColor: '#0f172a',
                  transform: 'translateX(-50%) translateY(-4px)',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.4)',
                }
              }}
            >
              Review Order Summary
            </Button>
          </Box>
      </Paper>
    </Box>
  );
}

interface StructDataType {
  order_data?: OrderData;
  [key: string]: any;
}

function OrderPage() {
  const auth = useContext(AuthContext);
  if (!auth) {
    return (
      <Box sx={{ textAlign: "center", marginTop: 5 }}>
        <Typography variant="h6">Authentication context not available</Typography>
      </Box>
    );
  }
  const { isAuthinticated } = auth;
  const [structData, setStructData] = useState<StructDataType>({});
  const [error, setError] = useState<string | null>(null);

  // New state for form fields

  const getStructuredData = async () => {
    try {
      const bdroute = `${BackendBaseUrl}order`;
      const response = await fetch(bdroute, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });  
      const responseData = await response.json();
      if (responseData.status === "success") {
        
        setStructData(responseData.strctured_data);
        localStorage.setItem(
          "structuredData",
          JSON.stringify(responseData.strctured_data)
        );
        
        setError(null);
      } else {
        setError("Error in fetching data");
      }
    } catch (error) {
      setError("Server Not Found");
    }
  };

  useEffect(() => {
    getStructuredData();
  }, []);

  return (
    <Box>
      <HeaderAppBar />

      {isAuthinticated ? (
        error ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              marginTop: 30,
            }}
          >
            <Typography variant="h4">{error}</Typography>
          </Box>
        ) : structData.order_data ? (
          <Box>
            <GenerateOrderCards order_data={structData.order_data} />
            <ScrollToTopButton />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              marginTop: 30,
            }}
          >
            <Typography>Loading...</Typography>
          </Box>
        )
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            marginTop: 30,
          }}
        >
          <Typography variant="h4">Not Allowed</Typography>
        </Box>
      )}
    </Box>
  );
}

export default OrderPage;
